import { CountryCode, parsePhoneNumberWithError } from "libphonenumber-js";
import { ExtensionStatus } from "../redux/slices/apps.slice";

export class Utils {
  static formatWaitTime(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    let str = "";
    if (h > 0) str += `${h}h `;
    if (m > 0 || h > 0) str += `${m}m `;
    str += `${s}s`;
    return str.trim();
  }
  static getExtensionStatus = (status: string): ExtensionStatus => {
    switch (status?.toLowerCase()) {
      case "answered":
        return ExtensionStatus.InCall;
      case "ringing":
        return ExtensionStatus.Ringing;
      default:
        return ExtensionStatus.Available;
    }
  };

  static getCallStatus = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'ringing':
        return 'ringing';
      case 'answered':
        return 'answered';
      case 'ended':
        return 'ended';
      case 'busy':
        return 'busy';
      case 'failed':
        return 'failed';
      default:
        return 'unknown';
    }
  };

  static renderStatus(status: string): string {
    if (status === ExtensionStatus.Available || status === ExtensionStatus.Offline || status === "Logged Out") {
        return '';
    }
    return status;
  }

  static isPossiblePhoneNumber = (phoneNumber: string): boolean => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phoneNumber?.replace(/[\s\-\(\)]/g, '') || '');
  };

  static isValidPhoneNumberForCountry(phoneNumberString: string, country: CountryCode = 'US'): boolean {
    try {
      const phoneNumber = parsePhoneNumberWithError(phoneNumberString, {
        defaultCountry: country,
        extract: false
      });
  
      if (!phoneNumber || phoneNumber.country !== country) {
        return false;
      }
  
      return phoneNumber.isValid();
    } catch {
      return false;
    }
  }

  static phoneNumberFormatE164 = (phoneNumber: string, countryCode: string = 'US'): string => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10 && countryCode === 'US') {
      return `+1${cleaned}`;
    }
    if (cleaned.startsWith('1') && cleaned.length === 11) {
      return `+${cleaned}`;
    }
    return phoneNumber;
  };

  static formatPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phoneNumber;
  };

  static formatSeconds = (seconds: number): string => {
    if (!seconds || seconds < 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  static formatDateTime = (dateString: string): string => {
    if (!dateString) return new Date().toLocaleDateString();
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
  };

  static formatCallDuration = (duration: number): string => {
    if (!duration) return '0:00';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  static hashCode = (str: string): number => {
    let hash = 0;
    if (str.length === 0) return hash;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash;
  };

  static generateNameSpaceColor = (name: string): string => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57',
      '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43',
      '#10AC84', '#EE5A24', '#0984E3', '#6C5CE7', '#A29BFE'
    ];
    const hash = Utils.hashCode(name);
    return colors[Math.abs(hash) % colors.length];
  };

  static memoryProfiler = {
    startProfiling: () => {
      if ((performance as any).memory) {
        return {
          used: (performance as any).memory.usedJSHeapSize,
          total: (performance as any).memory.totalJSHeapSize,
          limit: (performance as any).memory.jsHeapSizeLimit
        };
      }
      return null;
    },

    endProfiling: (startMemory: any) => {
      if ((performance as any).memory && startMemory) {
        const endMemory = {
          used: (performance as any).memory.usedJSHeapSize,
          total: (performance as any).memory.totalJSHeapSize,
          limit: (performance as any).memory.jsHeapSizeLimit
        };
        return {
          start: startMemory,
          end: endMemory,
          difference: endMemory.used - startMemory.used
        };
      }
      return null;
    }
  };

  static useMemoryProfiler = () => {
    const startMemory = Utils.memoryProfiler.startProfiling();
    return () => Utils.memoryProfiler.endProfiling(startMemory);
  };

  static withMemoryProfiler = (fn: Function, label: string = 'Function') => {
    return (...args: any[]) => {
      const startMemory = Utils.memoryProfiler.startProfiling();
      const result = fn(...args);
      const memoryDiff = Utils.memoryProfiler.endProfiling(startMemory);
      return result;
    };
  };

  /**
 * Converts an array of objects into a hashmap based on a given key.
 *
 * @param {Array} array - The array of objects.
 * @param {string} key - The property name to use as the hashmap key.
 * @returns {Object} - The resulting hashmap.
 */
  static arrayToHashmap = <
    T extends Record<string, any>,
    K extends keyof T
  >(
    array: T[],
    key: K
  ): Record<T[K] & (string | number | symbol), T> => {
    return array?.reduce((hashmap, item) => {
      const k = item[key];
      if (k !== undefined) {
        hashmap[k as T[K] & (string | number | symbol)] = item;
      }
      return hashmap;
    }, {} as Record<T[K] & (string | number | symbol), T>);
  };
}
